import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import LogoLoader from './LogoLoader';
import {
  alignShowDelay,
  FAILSAFE_DELAY_MS,
  getAnimationStart,
  hasFailsafeAnimationStarted,
  hasServerFailsafeStarted,
  LOADING_SCREEN_SELECTOR,
  SHOW_DELAY_MS,
  SHOW_DELAY_PROPERTY,
} from './showDelay';
import * as styles from './LoadingScreen.module.scss';

const { loadingScreen, loadingScreenScripted, loadingScreenContent, loadingScreenExiting, loadingScreenReleasedEarly } =
  styles;

// Must match $exit-duration and $early-exit-duration in LoadingScreen.module.scss.
export const LOADING_SCREEN_EXIT_MS = 400;
const EARLY_EXIT_MS = 150;
// Once it has appeared, the screen stays at least this long so it never reads as a flash.
const MIN_SHOWN_MS = 1000;

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const now = () => (typeof performance === 'undefined' ? Date.now() : performance.now());

// The failsafe is the container's only animation (the show animation runs on its content).
const getFailsafeAnimation = (container) => {
  const animations = container?.getAnimations?.() ?? [];
  return animations[animations.length - 1];
};

// Once its delay has elapsed the failsafe is already revealing the page, because script
// arrived too late to take over. Only for screens script mounts; a server-rendered one is
// judged by isServerFailsafeRevealing, as its progress can be unknown.
const hasFailsafeStarted = (container) => hasFailsafeAnimationStarted(getFailsafeAnimation(container));

// Shared by every instance for the rest of the page load. React can replace the
// server-rendered screen with a fresh one (when hydration fails it re-renders the tree
// from scratch), and a fresh element carries neither the failsafe's progress nor the
// show delay's clock, so both are read from the server-rendered screen as the bundle loads.
// `showsAt` is when the loader appears (or appeared), on the performance.now() clock.
const pageLoad = {
  revealed: false,
  showsAt: null,
  serverRendered: null,
  serverFailsafeStartedAt: null,
  serverFailsafeCancelled: false,
};

if (typeof document !== 'undefined') {
  const serverRendered = document.querySelector(LOADING_SCREEN_SELECTOR);
  if (serverRendered) {
    const failsafe = getFailsafeAnimation(serverRendered);
    pageLoad.serverRendered = serverRendered;
    pageLoad.serverFailsafeStartedAt = getAnimationStart(failsafe);
    pageLoad.showsAt = alignShowDelay(document.documentElement, failsafe) ?? SHOW_DELAY_MS;
  }
}

// The page counts as covered from its first paint until the cover is revealed, by release or
// by the failsafe, and never again after that.
const coverListeners = new Set();

export const isPageCovered = () => !pageLoad.revealed;

export const subscribeToPageCover = (listener) => {
  coverListeners.add(listener);
  return () => coverListeners.delete(listener);
};

const revealPage = () => {
  if (pageLoad.revealed) return;
  pageLoad.revealed = true;
  coverListeners.forEach((listener) => listener());
};

const isServerFailsafeRevealing = () => {
  if (!pageLoad.serverRendered || pageLoad.serverFailsafeCancelled) return false;
  if (pageLoad.serverFailsafeStartedAt !== null) {
    return now() - pageLoad.serverFailsafeStartedAt >= FAILSAFE_DELAY_MS;
  }
  return hasServerFailsafeStarted(getFailsafeAnimation(pageLoad.serverRendered));
};

// Full-screen cover for an app's first load. Each app decides when its first view is
// ready and flips `active` off. The cover is a blank, opaque surface from its first paint,
// so the page is never seen before it; the loader on it only appears once the page has
// been loading for SHOW_DELAY_MS (counted from navigation start). A load ready before then
// only sees the blank, which clears quickly; once the loader has appeared it stays at
// least MIN_SHOWN_MS, then the cover fades out and unmounts.
// `onMaxShown` is called once the loader has been showing for `maxShownMs`, so each app
// can cap how long it waits; the cap counts from when the loader appeared, so its
// phrases get the same time on screen however late that was.
// A CSS-only failsafe hides a server-rendered screen if script never takes over (JS slow
// to arrive or failing after SSR). Once mounted, script cancels it so the page is never
// revealed ahead of the app's own release logic.
// Once revealed, by release or by the failsafe, it never comes back on that page load.
const LoadingScreen = ({ active, label, phrases, translations, maxShownMs, onMaxShown, size = 96 }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const onMaxShownRef = useRef(onMaxShown);
  const [isShowing, setIsShowing] = useState(active);
  const [isMounted, setIsMounted] = useState(() => active && !pageLoad.revealed);
  const [isScripted, setIsScripted] = useState(false);
  const [wasRevealedByFailsafe, setWasRevealedByFailsafe] = useState(false);
  const [wasReleasedEarly, setWasReleasedEarly] = useState(false);
  const showing = isShowing && !wasRevealedByFailsafe;

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    // Left as it is while its own failsafe is revealing, so script never cancels that and
    // puts the cover back.
    const isOwnFailsafeRevealing =
      container === pageLoad.serverRendered ? isServerFailsafeRevealing() : hasFailsafeStarted(container);
    if (pageLoad.revealed || isOwnFailsafeRevealing || isServerFailsafeRevealing()) {
      revealPage();
      if (isOwnFailsafeRevealing) {
        setWasRevealedByFailsafe(true);
      } else {
        setIsMounted(false);
      }
      return undefined;
    }

    // The bundle can load before the server-rendered screen's start is known; by now it is.
    if (container === pageLoad.serverRendered) {
      pageLoad.showsAt = alignShowDelay(document.documentElement, getFailsafeAnimation(container)) ?? pageLoad.showsAt;
    }
    pageLoad.serverFailsafeCancelled = true;
    if (pageLoad.showsAt === null) {
      pageLoad.showsAt = Math.max(now(), SHOW_DELAY_MS);
    }
    setIsScripted(true);
    if (container === pageLoad.serverRendered) return undefined;

    // A screen script mounts starts its CSS clock about now, so shift its show delay (and
    // the mark's loop with it) onto the page load's clock. Negative when it replaces a screen
    // that was already showing, so it carries on where that one was. The exact start is only
    // known once the browser starts its animations (Chrome can backdate them by a frame or
    // more), so the delay is corrected then if the loader hasn't appeared yet.
    const applyShowDelay = (startedAt) =>
      container.style.setProperty(SHOW_DELAY_PROPERTY, `${Math.round(pageLoad.showsAt - startedAt)}ms`);
    applyShowDelay(now());
    let frameId = null;
    const correctShowDelay = () => {
      const startedAt = contentRef.current?.getAnimations?.()[0]?.startTime;
      if (now() >= pageLoad.showsAt) return;
      if (typeof startedAt === 'number') {
        applyShowDelay(startedAt);
        return;
      }
      frameId = requestAnimationFrame(correctShowDelay);
    };
    if (typeof requestAnimationFrame === 'function') frameId = requestAnimationFrame(correctShowDelay);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    onMaxShownRef.current = onMaxShown;
  });

  useEffect(() => {
    if (!active || !maxShownMs || pageLoad.revealed || pageLoad.showsAt === null) return undefined;

    const timeoutId = setTimeout(() => onMaxShownRef.current?.(), Math.max(0, pageLoad.showsAt + maxShownMs - now()));
    return () => clearTimeout(timeoutId);
  }, [active, maxShownMs]);

  useEffect(() => {
    if (active) {
      if (pageLoad.revealed) return undefined;
      setIsShowing(true);
      setIsMounted(true);
      return undefined;
    }

    if (pageLoad.showsAt === null || now() < pageLoad.showsAt) {
      setWasReleasedEarly(true);
      setIsShowing(false);
      return undefined;
    }

    const remainingMs = pageLoad.showsAt + MIN_SHOWN_MS - now();
    const timeoutId = setTimeout(() => setIsShowing(false), Math.max(0, remainingMs));
    return () => clearTimeout(timeoutId);
  }, [active]);

  useEffect(() => {
    if (showing) return undefined;

    revealPage();

    const timeoutId = setTimeout(() => setIsMounted(false), wasReleasedEarly ? EARLY_EXIT_MS : LOADING_SCREEN_EXIT_MS);
    return () => clearTimeout(timeoutId);
  }, [showing, wasReleasedEarly]);

  if (!isMounted) return null;

  return (
    <div
      ref={containerRef}
      className={[
        loadingScreen,
        isScripted && loadingScreenScripted,
        !showing && loadingScreenExiting,
        wasReleasedEarly && loadingScreenReleasedEarly,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="first-load-screen"
      aria-busy={showing}
    >
      <div ref={contentRef} className={loadingScreenContent}>
        <LogoLoader size={size} label={label} phrases={phrases} translations={translations} />
      </div>
    </div>
  );
};

export default LoadingScreen;
